import React, { FC } from 'react';
import styled from '@emotion/styled';
import Heading from '~ComponentsImproved/Typography/Heading';
import useI18n from '~Translations';
import { combineArticles } from '~Utils/combineArticles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useCurrency } from '~Hooks';
import { Box } from '~Styles';

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  padding-bottom: 40px;

  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    width: 100%;
    height: 40px;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(to top, rgba(28 35, 44, 1) 0%, rgba(14, 18, 22, 0) 100%);
  }
`;

const InnerWrapper = styled.div`
  overflow: auto;
  height: 100%;
  padding-bottom: 40px;
`;

const Row = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  gap: 4px;

  &:first-of-type {
    padding-top: 0;
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--colorBlueGray1000);
  }
`;

interface Props {
  selectedItems: any[];
  amount: number;
  type: 'ITEM' | 'ORDER' | 'TIP';
  tipAmount?: number;
}

const Summary: FC<Props> = ({ type, amount, selectedItems, tipAmount }) => {
  const { i18n } = useI18n();
  const currency = useCurrency();

  const HEADINGS = {
    ORDER: i18n('Refunds.FullRefund.Title'),
    ITEM: i18n('Refunds.PartRefund.Title'),
    TIP: i18n('Refunds.TipRefund.Title'),
  };

  const formattedItems = selectedItems.map((item) => ({
    itemId: item.entry.item.id,
    title: i18n(item.entry.item.titleLang),
    amount: item.price.amount,
    discount: item.price.discount,
    variations: item.variations,
  }));

  const combinedItems = combineArticles(formattedItems);

  return (
    <Wrapper>
      <InnerWrapper>
        <Heading level={2} color="var(--colorBlueGray50)" bottom={24}>
          {HEADINGS[type]}
        </Heading>
        {type === 'TIP' ? (
          <Row>
            <Heading level={4} color="var(--colorBlueGray50)">
              {i18n('Refunds.Summary.TipToRefund')}:
            </Heading>
            <Paragraph level={3} color="var(--colorBlueGray50)">
              {currency.format(amount)}
            </Paragraph>
          </Row>
        ) : (
          <>
            <Heading level={4} color="var(--colorBlueGray50)" bottom={16}>
              {i18n('Refunds.Summary.ArticlesToRefund')}:
            </Heading>
            {combinedItems.map((item) => (
              <Row key={item.itemId}>
                <Heading level={5} color="var(--colorBlueGray50)">
                  x{item.count} {item.title}
                </Heading>
                <Paragraph level={4} color="var(--colorBlueGray50)">
                  {currency.format(item.count * (item.amount - item.discount))}
                </Paragraph>
              </Row>
            ))}

            {type === 'ORDER' && tipAmount ? (
              <Box marginTop="small">
                <Row>
                  <Heading level={4} color="var(--colorBlueGray50)" bottom={16}>
                    {i18n('Refunds.Summary.TipToRefund')}:
                  </Heading>
                  <Paragraph level={3} color="var(--colorBlueGray50)">
                    {currency.format(tipAmount)}
                  </Paragraph>
                </Row>
              </Box>
            ) : null}
          </>
        )}
      </InnerWrapper>
    </Wrapper>
  );
};

export default Summary;
